import React, { useEffect, useState, useCallback, useRef } from 'react';
import styles from '@/components/Dictionary/Dictionary.module.less';
import { getDictionaryTrending, getAITranslationTrending } from '@/libs/api';
import { FireOutlined, HistoryOutlined, ReloadOutlined, UnorderedListOutlined} from '@ant-design/icons';
import { throttle } from 'throttle-debounce';
import { useNavigate } from 'react-router-dom';

// Trending数据接口定义
interface TrendingItem {
  query: string;
  word?: {
    id: number;
    w: string;
    m: string;
  };
  useCount: number;
  latestAt: Date | string;
}

interface LatelySearchProps {
  title?: string;
  type?: 'all' | 'trending' | 'recent';
  dataSource: 'dictionary' | 'ai';
  onWordClick?: (word: string) => void;
  className?: string;
}

// 为节流函数定义一个类型
type ThrottledFunction = {
  (): void;
  cancel?: () => void;
};

const LatelySearch: React.FC<LatelySearchProps> = ({
  title = '最新查询',
  type = 'recent',
  dataSource,
  onWordClick,
  className
}) => {
  const [searchHistory, setSearchHistory] = useState<TrendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // 创建一个 ref 来存储节流函数
  const throttledRefreshRef = useRef<ThrottledFunction | null>(null);

  const fetchSearchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = dataSource === 'ai'
        ? await getAITranslationTrending(type, 16)
        : await getDictionaryTrending(type, 16);

      // 处理各种可能的数据格式
      if (data && data.result && Array.isArray(data.result.items)) {
        // 标准格式
        setSearchHistory(data.result.items);
      } else if (data && Array.isArray(data)) {
        // 数组格式
        setSearchHistory(data);
      } else if (data && typeof data === 'object') {
        // 尝试其他可能的格式
        const items = data.items || data.result || [];
        setSearchHistory(Array.isArray(items) ? items : []);
      } else {
        setSearchHistory([]);
        setError('未获取到数据');
      }
    } catch (error) {
      console.error('获取trending数据失败:', error);
      setError('获取trending数据失败');
      setSearchHistory([]);
    } finally {
      setLoading(false);
    }
  }, [type, dataSource]);

  // 使用 useEffect 在组件挂载时获取搜索历史
  useEffect(() => {
    if (throttledRefreshRef.current === null) {
      fetchSearchHistory();
      // 创建节流函数并存储在 ref 中，这样它在组件的整个生命周期内只会创建一次
      throttledRefreshRef.current = throttle(2000, () => {
        setLoading(true);
        fetchSearchHistory();
      });
    }

    // 组件卸载时清理
    return () => {
      if (throttledRefreshRef.current?.cancel) {
        throttledRefreshRef.current.cancel();
      }
    };
  }, [fetchSearchHistory]);

  // 处理刷新按钮点击
  const handleRefresh = useCallback(() => {
    if (throttledRefreshRef.current) {
      throttledRefreshRef.current();
    }
  }, [] );

  const handleViewAll = () => {
    navigate( `/history` );
    // TODO: 页面UI优化
    // 根据dataSource跳转到对应的历史页面
    // navigate(`/history/${dataSource}`);
  };

  const handleWordClick = (query: string) => {
    if (onWordClick) {
      onWordClick(query);
    }
  };

  // 始终渲染组件，但在没有数据时显示不同的内容
  return (
    <div className={`${styles.latelySearchContainer} ${className}`}>
      {title && (
        <div className={styles.latelySearchHeader}>
          <div>
            <span className={styles.latelySearchTitle}>
              {type === 'trending' ? (
                <FireOutlined className={styles.latelySearchIcon} />
              ) : (
                <HistoryOutlined className={styles.latelySearchIcon} />
              )}
              {title}
            </span>
            {/* TODO: bugfix - 无法刷新数据问题 */}
            {/* <span className={styles.refetchIcon} onClick={handleRefresh}>
              <ReloadOutlined />
            </span> */}
          </div>
          <span className={styles.refetchIcon} onClick={handleViewAll}>
            <UnorderedListOutlined />
          </span>
        </div>
      )}

      <div className={styles.latelySearchList}>
        {loading ? (
          <div className={styles.latelySearchLoading}>加载中...</div>
        ) : error ? (
          <div className={styles.latelySearchError}></div>
        ) : searchHistory.length === 0 ? (
          <div className={styles.latelySearchEmpty}>暂无查词记录</div>
        ) : (
          searchHistory.map((item, index) => (
            <div
              key={`${item.query}-${index}`}
              className={styles.latelySearchItem}
              onClick={() => handleWordClick(item.query || item.word?.w || '')}
            >
              {item.word?.w || item.query || '未知词条'}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LatelySearch;
